"use client";

import { useEffect, useState } from "react";
import { signIn } from "next-auth/react";
import { useRouter, useSearchParams } from "next/navigation";
import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import { useLocale } from "@/components/locale-provider";
import { POST_SIGN_IN_PATH } from "@/lib/post-sign-in";
import { generateCode, sha256Base64Url } from "@/lib/native-auth-crypto";
import { cancelNativeSignIn, isNativeSignInInFlight, startNativeSignIn } from "@/lib/native-auth-state";

/**
 * `showOAuth` is decided by the server (page.tsx) from the request host, not
 * here — see the comment on the OAuth block below for why it is off on the
 * console host.
 */
export function LoginForm({ showOAuth, showApple }: { showOAuth: boolean; showApple: boolean }) {
  const router = useRouter();
  const searchParams = useSearchParams();
  // Defaulting to "/" sent a user who had just typed their password to the
  // marketing landing page, for the same reason the native bridge did — see
  // post-sign-in.ts. An explicit callbackUrl (the console host's proxy adds
  // one) still wins.
  const callbackUrl = searchParams.get("callbackUrl") || POST_SIGN_IN_PATH;
  const { t } = useLocale();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (searchParams.get("error") === "GoogleNotLinked") {
      setError(t.login.googleNotLinked);
    } else if (searchParams.get("error") === "AppleNotLinked") {
      setError(t.login.appleNotLinked);
    } else if (searchParams.get("error") === "EmailAlreadyRegistered") {
      setError(t.login.emailAlreadyRegistered);
    } else if (searchParams.get("error") === "RegistrationClosed") {
      setError(t.login.registrationClosed);
    } else if (searchParams.get("error") === "GoogleEmailUnverified") {
      setError(t.login.googleEmailUnverified);
    } else if (searchParams.get("error") === "RegistrationRateLimited") {
      setError(t.login.registrationRateLimited);
    } else if (searchParams.get("error") === "RegistrationFailed") {
      setError(t.login.registrationFailed);
    }
  }, [
    searchParams,
    t.login.googleNotLinked,
    t.login.appleNotLinked,
    t.login.emailAlreadyRegistered,
    t.login.registrationClosed,
    t.login.googleEmailUnverified,
    t.login.registrationRateLimited,
    t.login.registrationFailed,
  ]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const res = await signIn("credentials", {
      username,
      password,
      redirect: false,
    });

    setLoading(false);

    // Throttled by the rate limiter in the credentials callback route —
    // reported separately so a locked-out user isn't told their (correct)
    // password is wrong.
    if (res?.status === 429) {
      setError(t.login.tooManyAttempts);
      return;
    }

    if (res?.error) {
      setError(t.login.invalidCredentials);
      return;
    }

    router.push(callbackUrl);
    router.refresh();
  };

  // Google refuses OAuth inside embedded WebViews, so on native the flow runs
  // in a real browser and comes back through the verified App Link
  // /native/auth — see native-auth-return.ts. The verifier never leaves the
  // app: it is what makes an intercepted code unusable.
  //
  // The browser is sent to our own start route (which lands on /login?native=1),
  // NOT straight at Auth.js's signin endpoint: a GET there renders Auth.js's own
  // sign-in page rather than starting the flow, which needs a CSRF-bearing POST.
  // Loading /login lets the page below start it properly with the handoff as its
  // callbackUrl.
  const handleGoogle = async () => {
    if (!Capacitor.isNativePlatform()) {
      void signIn("google", { callbackUrl });
      return;
    }
    // A second tap would overwrite the stored verifier, and the first flow's
    // code — already minted against the first challenge — could then never be
    // redeemed. The user would get a generic failure with nothing to explain
    // it. An earlier iOS attempt at this feature hit the same class of bug
    // (branch `native-google-signin`, commits 31d4491 and 4501f2c): reject the
    // second call rather than letting it clobber the flow in flight.
    //
    // The guard is re-armed in native-auth-bridge.tsx once the Custom Tab is
    // known to be closed without a completed sign-in — not here, and not on
    // a timer, since only the bridge (which owns the deep-link listener)
    // knows when that has genuinely happened.
    if (isNativeSignInInFlight()) {
      // Logged because this return is otherwise completely silent: a guard
      // left armed by an earlier failure makes the button look broken, with
      // nothing anywhere to say why.
      console.error("[native-auth] ignored tap: a sign-in is already in flight");
      return;
    }
    startNativeSignIn();

    try {
      // Everything below is inside the try, not just Browser.open. These
      // three lines used to sit outside it, so a throw from crypto.subtle
      // (which is availability-sensitive in a WebView) armed the guard and
      // then escaped — leaving the button dead for the rest of the WebView's
      // life with no log and no visible failure. Observed on a Pixel 6a.
      const verifier = generateCode();
      sessionStorage.setItem("native-auth-verifier", verifier);
      const challenge = await sha256Base64Url(verifier);
      // /api/auth/native/start, not /login directly: only a route handler can
      // set the HttpOnly cookie that binds this challenge to the browser leg,
      // and the mint route refuses to mint without it. /start writes the
      // cookie and bounces straight on to /login?native=1&challenge=…, which
      // is what the effect below picks up.
      await Browser.open({
        url: `${window.location.origin}/api/auth/native/start?challenge=${encodeURIComponent(challenge)}`,
      });
    } catch (err) {
      // Nothing else can re-arm the guard on this path. `browserFinished`
      // only fires for a tab that actually opened, so a throw here left
      // `nativeSignInInFlight` true for the rest of the WebView's life and
      // the button dead — every later tap returning at the guard above — with
      // the rejection unhandled on top. The stranded verifier goes too, for
      // the same reason native-auth-bridge.tsx clears it on cancellation:
      // this attempt is over and it can only confuse the next one.
      sessionStorage.removeItem("native-auth-verifier");
      cancelNativeSignIn();
      console.error("[native-auth] could not open the sign-in browser:", err);
    }
  };

  // Runs in the BROWSER copy of this page, opened above. Starts the ordinary
  // web Google flow, but points its callbackUrl at the mint route so success
  // lands on the /native/auth return redirect instead of the library.
  const nativeChallenge = searchParams.get("challenge");
  const isNativeHandoff = searchParams.get("native") === "1" && nativeChallenge !== null;
  useEffect(() => {
    if (!isNativeHandoff || Capacitor.isNativePlatform()) return;
    void signIn("google", {
      callbackUrl: `/api/auth/native/handoff?challenge=${encodeURIComponent(nativeChallenge)}`,
    });
  }, [isNativeHandoff, nativeChallenge]);

  return (
    <div className="safe-top safe-bottom relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-ios-bg px-6">
      <div className="pointer-events-none absolute -top-32 -left-24 h-80 w-80 rounded-full bg-ios-accent/30 blur-[100px]" />
      <div className="pointer-events-none absolute -right-24 -bottom-32 h-80 w-80 rounded-full bg-ios-accent/20 blur-[100px]" />

      <div className="relative mb-8 flex flex-col items-center gap-3">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src="/logo.webp" alt="" className="h-16 w-16 rounded-[18px] shadow-lg shadow-ios-accent/30" />
        <h1 className="text-[22px] font-semibold text-ios-label">Rakemori</h1>
        <p className="text-[13px] text-ios-label-secondary">{t.login.subtitle}</p>
      </div>

      <form onSubmit={handleSubmit} className="relative w-full max-w-sm space-y-5">
        {error && (
          <p className="rounded-ios-md bg-ios-destructive/10 px-4 py-2.5 text-center text-[13px] font-medium text-ios-destructive">
            {error}
          </p>
        )}

        <div className="glass-floating glass-specular overflow-hidden rounded-ios-md">
          <div className="border-b border-ios-separator/50 px-4 py-2.5">
            <label htmlFor="username" className="block text-[11px] text-ios-label-secondary">
              {t.login.username}
            </label>
            <input
              id="username"
              className="w-full bg-transparent text-[16px] text-ios-label outline-none"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              autoCapitalize="off"
              autoCorrect="off"
              required
            />
          </div>
          <div className="px-4 py-2.5">
            <label htmlFor="password" className="block text-[11px] text-ios-label-secondary">
              {t.login.password}
            </label>
            <input
              id="password"
              type="password"
              className="w-full bg-transparent text-[16px] text-ios-label outline-none"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="glass-specular press-shrink w-full rounded-ios-md bg-ios-accent px-4 py-3 text-[16px] font-semibold text-white shadow-lg shadow-ios-accent/25 disabled:opacity-50"
        >
          {loading ? t.login.signingIn : t.login.signIn}
        </button>
      </form>

      {/* Credentials-only on the console host: only the apex is registered as a
          redirect URI with Google and Apple, so a flow started on the console is
          rejected with `redirect_uri_mismatch` before any code is exchanged.
          Rather than show two buttons that are guaranteed to break, the console
          offers only credentials. Nothing changes on the apex. */}
      {showOAuth && (
        <>
          <div className="relative mt-6 flex w-full max-w-sm items-center gap-3">
            <div className="h-px flex-1 bg-ios-separator" />
            <span className="text-[12px] text-ios-label-tertiary">{t.login.orDivider}</span>
            <div className="h-px flex-1 bg-ios-separator" />
          </div>

          <button
            type="button"
            onClick={handleGoogle}
            className="press-shrink relative mt-6 flex w-full max-w-sm items-center justify-center gap-2 rounded-ios-md border border-ios-separator bg-ios-card px-4 py-3 text-[15px] font-medium text-ios-label"
          >
            <svg width="18" height="18" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M23.52 12.27c0-.85-.08-1.67-.22-2.45H12v4.64h6.47a5.53 5.53 0 0 1-2.4 3.63v3.02h3.87c2.27-2.09 3.58-5.17 3.58-8.84Z"
              />
              <path
                fill="#34A853"
                d="M12 24c3.24 0 5.96-1.07 7.95-2.9l-3.87-3.02c-1.08.72-2.45 1.15-4.08 1.15-3.13 0-5.79-2.12-6.74-4.96H1.27v3.12A12 12 0 0 0 12 24Z"
              />
              <path
                fill="#FBBC05"
                d="M5.26 14.27a7.2 7.2 0 0 1 0-4.54V6.6H1.27a12 12 0 0 0 0 10.79l3.99-3.12Z"
              />
              <path
                fill="#EA4335"
                d="M12 4.77c1.76 0 3.34.6 4.59 1.79l3.44-3.44C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.69 1.27 6.6l3.99 3.13C6.21 6.89 8.87 4.77 12 4.77Z"
              />
            </svg>
            {t.login.signInWithGoogle}
          </button>

          {/* Hidden until Apple Developer credentials exist. The provider in
              auth.ts is registered on the same predicate, so without them
              "apple" is not a provider at all and this button could only fail.
              Both come back on their own once APPLE_ID/APPLE_SECRET are set. */}
          {showApple && (
          <button
            type="button"
            onClick={() => signIn("apple", { callbackUrl })}
            className="press-shrink relative mt-3 flex w-full max-w-sm items-center justify-center gap-2 rounded-ios-md border border-ios-separator bg-ios-label px-4 py-3 text-[15px] font-medium text-ios-bg"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
              {/* Path is inset in the 24×24 box vs Google's full-bleed G — scale to match optical size. */}
              <g transform="translate(12 12) scale(1.45) translate(-12 -12)">
                <path d="M16.37 12.63c.02 2.3 2.02 3.07 2.04 3.08-.02.05-.32 1.1-1.05 2.18-.63.93-1.29 1.85-2.32 1.87-.98.02-1.3-.59-2.42-.59s-1.49.57-2.43.61c-.97.03-1.72-.99-2.36-1.91-1.3-1.89-2.3-5.34-.96-7.25.66-.95 1.84-1.55 3.12-1.57 1 .02 1.95.68 2.42.68.47 0 1.64-.84 2.77-.71.47.02 1.79.19 2.64 1.45-.07.04-1.58.92-1.56 2.76ZM14.7 6.45c.53-.65.89-1.54.79-2.45-.77.03-1.7.52-2.26 1.16-.49.56-.93 1.47-.81 2.34.86.07 1.74-.44 2.28-1.05Z" />
              </g>
            </svg>
            {t.login.signInWithApple}
          </button>
          )}
        </>
      )}
    </div>
  );
}
